import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { findIndex, filter, map, extend, find, chain, where, isEmpty } from 'underscore';
import { DefaultButton, Panel, PanelType, Stack, PrimaryButton, MessageBar, MessageBarType } from '@fluentui/react';
import { IconButton } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FluentImagePicker } from '@bingads-webui-campaign-react/fluent-image-picker';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { panelStylesProp } from '@bingads-webui-campaign-react/asset-library-shared-add-media-layout';
import { CopilotTab, CopilotTabItem } from '@bingads-webui/inline-copilot-ui';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { getImageSuggestionPanelStyles, imageSuggestionPanelWidth } from './style';

const getImageWithDefaultSelectState = images => map(images, image => extend({}, image, { isSelected: false }));

const mapSelectSate = (allImages, needUpdateImages) => map(needUpdateImages, (image) => {
  const newImage = find(allImages, img => img.fullUrl === image.fullUrl);
  return newImage || image;
});

export const ImageRecommendationsPanel = (props) => {
  const {
    i18n,
    title,
    images,
    themes,
    hasMore,
    showThemes,
    loadNextPage,
    onSelectImage,
    onDismiss,
    isMultiSelect,
    onAdd,
    maxUploadNum,
  } = props;

  const isLoadingFirstPage = false;

  const defaultImagesWithSelectState = useMemo(() => getImageWithDefaultSelectState(images), [images]);
  const classes = useDefaultStyles(getImageSuggestionPanelStyles);
  const [error, setError] = useState(null);
  const [allImages, setAllImages] = useState(defaultImagesWithSelectState);
  const [activeImages, setActiveImages] = useState(allImages);
  const [activeTheme, setActiveTheme] = useState(i18n.getString(_TL_('All')));
  const themeNames = useMemo(() => [
    ...map(themes.filter(theme => !isEmpty(theme.images)), ({ name }) => name),
  ], [themes]);

  const onSwitchTheme = useCallback((key) => {
    setActiveTheme(key);
    if (key === i18n.getString(_TL_('All'))) {
      setActiveImages(allImages);
    } else {
      const rawImages = chain(themes)
        .filter(theme => theme.name === key)
        .first()
        .result('images')
        .map(img => extend({}, img))
        .value();
      const imagesWithState = mapSelectSate(allImages, rawImages);
      setActiveImages(imagesWithState);
    }
  }, [allImages, i18n, themes]);

  const onClickAdd = useCallback(() => {
    onAdd(filter(allImages, image => image.isSelected));
  }, [onAdd, allImages]);

  const onClickCancel = useCallback((e) => {
    onDismiss(e);
  }, [onDismiss]);

  const onRenderHeader = useCallback(() => (
    <div className={classes.panelHeader}>
      <div className={classes.backButton}>
        <IconButton aria-label={i18n.getString(_TL_('Back'))} iconProps={{ iconName: 'Back' }} className="back-button" onClick={onClickCancel} />
      </div>
      <div className={classes.panelTitle}>
        {i18n.getString(title)}
      </div>
    </div>
  ), [classes.backButton, classes.panelHeader, classes.panelTitle, i18n, onClickCancel, title]);

  const onRenderFooterContent = useCallback(() => (
    <Stack className={classnames('image-recommendations-panel-buttons', classes.buttonContainer)} horizontal tokens={{ childrenGap: 8 }}>
      { isMultiSelect &&
        <Stack.Item key="btn-add">
          <PrimaryButton className="btn-add" onClick={onClickAdd} disabled={findIndex(allImages, image => image.isSelected) === -1}>
            {i18n.getString(_TL_('Add'))}
          </PrimaryButton>
        </Stack.Item>
      }
      <Stack.Item key="btn-cancel">
        <DefaultButton className="btn-cancel" onClick={onClickCancel}>
          {i18n.getString(_TL_('Cancel'))}
        </DefaultButton>
      </Stack.Item>
      { isMultiSelect &&
        <Stack.Item key="selected-label">
          <span className="selected-label">{i18n.getString(_TL_('Selected images: {{count}}'), { count: where(allImages, { isSelected: true }).length })}</span>
        </Stack.Item>
      }
    </Stack>
  ), [classes.buttonContainer, isMultiSelect, onClickAdd, allImages, i18n, onClickCancel]);

  const onSelectImageInternal = useCallback((index) => {
    const { fullUrl, suggestionId } = isMultiSelect || showThemes ? activeImages[index] : images[index];

    if (!isMultiSelect) {
      onDismiss();
      onSelectImage(fullUrl, index, suggestionId);
    } else {
      const image = find(allImages, img => img.fullUrl === fullUrl);

      if (!image.isSelected && maxUploadNum !== undefined && filter(allImages, img => img.isSelected).length >= maxUploadNum) {
        setError(i18n.getString(_TL_('You\'ve exceeded the max. number of assets you can add at a time ({{limitNumber}}). Please try again.'), { limitNumber: maxUploadNum }));
        return;
      }

      setError(null);
      if (image) {
        image.isSelected = !image.isSelected;
      }
      setAllImages([...allImages]);
      setActiveImages(mapSelectSate(allImages, activeImages));
    }
  }, [isMultiSelect, showThemes, activeImages, images, onDismiss, onSelectImage, allImages, maxUploadNum, i18n]);

  return (
    <Panel
      onRenderHeader={onRenderHeader}
      isOpen
      onDismiss={onClickCancel}
      onRenderFooterContent={onRenderFooterContent}
      isFooterAtBottom
      type={PanelType.custom}
      customWidth={imageSuggestionPanelWidth}
      styles={panelStylesProp}
      className="image-recommendations-panel"
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
    >
      {(isMultiSelect || showThemes) &&
        <FluentProvider theme={webLightTheme}>
          <div className={classes.theme}>
            <CopilotTab
              active={activeTheme}
              onActiveChange={key => onSwitchTheme(key)}
            >
              <CopilotTabItem tabKey={i18n.getString(_TL_('All'))}>
                {i18n.getString(_TL_('All'))}
              </CopilotTabItem>
              {map(themeNames, name => (
                <CopilotTabItem tabKey={name}>
                  {name}
                </CopilotTabItem>
              ))}
            </CopilotTab>
          </div>
        </FluentProvider>
      }
      {
        isLoadingFirstPage ? <AssetLibrarySpinner useCustomSize customStyle={{ marginTop: '50px' }} /> :
        <FluentImagePicker
          images={isMultiSelect || showThemes ? activeImages : images}
          pageSize={30}
          loadMoreImages={loadNextPage}
          onSelectImage={onSelectImageInternal}
          hasMoreImages={hasMore}
          imageCellWidth={260}
          imageCellHeight={243}
          isMultiSelect={isMultiSelect}
        />
      }
      {
        error && <MessageBar messageBarType={MessageBarType.error} className={classes.errorMessage}>{error}</MessageBar>
      }
    </Panel>);
};

ImageRecommendationsPanel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  title: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape({
    thumbnailSrc: PropTypes.string,
    fullUrl: PropTypes.string,
    suggestionId: PropTypes.string,
  })).isRequired,
  showThemes: PropTypes.bool,
  themes: PropTypes.arrayOf(PropTypes.shape({})),
  hasMore: PropTypes.bool.isRequired,
  loadNextPage: PropTypes.func.isRequired,
  onSelectImage: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  isMultiSelect: PropTypes.bool,
  onAdd: PropTypes.func,
  maxUploadNum: PropTypes.number,
};

ImageRecommendationsPanel.defaultProps = {
  title: _TL_('Recommended images'),
  themes: [],
  showThemes: true,
  isMultiSelect: false,
  onAdd: () => {},
  maxUploadNum: undefined,
};
